import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import WPAgGrid from "../../packages/WPAgGrid/WPAgGrid";
import WPPagination from "../../packages/WPPagination";
import createNotification, { ERROR_NOTIFICATION } from "../Notifications";
import {
  CIP_SOURCE_OPTIONS,
  PAGE_SIZE_OPTIONS,
  DEfAULT_PAGE_SIZE_OPTION,
} from "../../constants/global.constants";
import { useSelector } from "react-redux";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import CIPEventDialog from "../CIPEventDialog/CIPEventDialog";
import { useTranslation } from "react-i18next";
import { getCIPs } from "../../network/cipAPI";
import { CIP_TABLE_CONFIG } from "../CIPSpecification/CIPTable.config";
import { errorIcon } from "../../assets";
import { storeCIPS } from "../../Store/TemplateStore/template.action";
import PlusIcon from "../../common/icons/PlusIcon";

import "./CIPPaginatedTable.scss";

const CIPPaginatedTable = ({
  selectedTrain,
  exportFileName,
  resetCIPMode,
  isScrollIntoView = false, // no scroll into view by default
  source = null, // all CIPs
  errorMessage = null, // No error message by default,
  enableActions = false, // Enable actions column if true
  enableAdd = false, // Enable add button if true
  showTableLoader = false, // Show loader for table
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { temperature, mass } = useSelector((state) => state);
  const gridRef = useRef(null);

  const [totalItems, setTotalItems] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // setting 50 as default for temporary
  const [pageSize, setPageSize] = useState(DEfAULT_PAGE_SIZE_OPTION);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const updateStoreCips = (data) => {
    dispatch(storeCIPS([...data]));
  };

  const showLoader = (loading) => {
    setIsLoading(loading);
  };

  const fetchCIPs = async () => {
    showLoader(true);
    try {
      const response = await getCIPs(
        selectedTrain,
        pageSize,
        currentPage,
        source
      );
      if (response.status === 200) {
        setTotalItems(response.data.total_cips);
        const CIPData = response.data.cips;
        updateStoreCips(CIPData);

        // reset when last item is deleted
        if (CIPData.length === 0) {
          setTableData([]);
          resetCIPMode && resetCIPMode();
        } else {
          const srNo = (currentPage - 1) * pageSize;
          const updatedCIPData = CIPData.map((cip, idx) => ({
            ...cip,
            srNo: srNo + idx + 1,
          }));
          setTableData(updatedCIPData);
        }
      }
    } catch (err) {
      setTableData([]);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong")
      );
    } finally {
      showLoader(false);
    }
  };

  useEffect(() => {
    if (selectedTrain) {
      fetchCIPs();
    }
  }, [pageSize, currentPage]);

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  return (
    <>
      {showAddDialog && (
        <CIPEventDialog
          onModalClose={() => setShowAddDialog(false)}
          refreshTable={fetchCIPs}
          source={source}
        />
      )}
      {errorMessage && !isLoading && tableData.length === 0 ? (
        <div className="errorText12 card-error">
          <img src={errorIcon} alt="Error" />
          {errorMessage}
        </div>
      ) : (
        <div className="cip-table-wrapper">
          {enableAdd && (
            <div className="cip-table-options">
              <WPTertiaryButton
                label={
                  source === CIP_SOURCE_OPTIONS.RECURRENT
                    ? t("CIPEvent.addRecurrentEvents")
                    : t("CIPEvent.addCIP/Shutdown")
                }
                onClick={() => setShowAddDialog(true)}
                icon={<PlusIcon size={16} />}
                iconPlacement="before"
                variant="outlined"
                size="small"
                className={exportFileName ? "cip-add-export-visible" : ""}
              />
            </div>
          )}
          <div className="cip-table">
            <WPAgGrid
              gridRef={gridRef}
              columnDefs={CIP_TABLE_CONFIG(
                t,
                temperature,
                mass,
                fetchCIPs,
                showLoader,
                source,
                enableActions
              )}
              rowData={tableData}
              exportFileName={exportFileName}
              loading={isLoading || showTableLoader}
              isScrollIntoView={isScrollIntoView}
            />
            <WPPagination
              totalItems={totalItems}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CIPPaginatedTable;
